import React, {
  useMemo,
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';

import useComponentMounted from '../../../../../hooks/useComponentMounted';
import { useClientMealPlans, withMealPlanContextReady } from '../../../../context/MealPlanContext';
import UserNutritionMacroGoals from '../../../../Model/UserNutritionMacroGoals';
import NutritionHistory from '../../../NutritionHistory';
import LoadingPage from '../../../../../components/LoadingPage';

import {
  Container,
  InfoContainer,
} from './styles';
import texts from './texts.json';

const NutritionalHistory = ({ user }) => {
  const clientAssignments = useClientMealPlans(user);

  const [macroGoalsDoc, setMacroGoalsDoc] = useState({});
  const [isReady, setIsReady] = useState(false);

  const isComponentMountedRef = useComponentMounted();

  useEffect(() => {
    const init = async () => {
      const doc = await UserNutritionMacroGoals.getById(user) || {};
      if (isComponentMountedRef.current) {
        setMacroGoalsDoc(doc);
        setIsReady(true);
      }
    };
    init();
  }, [
    user,
    isComponentMountedRef,
  ]);

  const { history = [] } = macroGoalsDoc;

  const currentNutritionalGoals = useMemo(() => {
    if (!macroGoalsDoc || !macroGoalsDoc.getCurrentGoals) {
      return [];
    }
    /*
      Flagged rather than inferred from position: every entry here is a live set of goals, and
      one client's plans all carry the same `updatedMacroAveragesAt` from the shared macro goals
      document, so neither the date nor the index can tell them apart.
    */
    return clientAssignments
      .filter(({ assignmentId }) => !!assignmentId)
      .map(({ assignmentId, assignmentName, assignmentMacroAverages }) => ({
        ...macroGoalsDoc.getCurrentGoals(assignmentId, assignmentName, assignmentMacroAverages),
        isCurrent: true,
      }));
  }, [
    clientAssignments,
    macroGoalsDoc,
  ]);

  const fullHistory = useMemo(() => (
    [...history, ...currentNutritionalGoals].reverse()
  ), [
    history,
    currentNutritionalGoals,
  ]);

  if (!isReady) {
    return <LoadingPage />;
  }

  return (
    <Container>
      <InfoContainer>
        {fullHistory.length > 0
          ? <NutritionHistory history={fullHistory} />
          : texts.emptyText}
      </InfoContainer>
    </Container>
  );
};

NutritionalHistory.propTypes = {
  user: PropTypes.string.isRequired,
};

export default compose(
  withMealPlanContextReady,
)(NutritionalHistory);
